var _excluded = ["kind", "variant", "type", "tooltip", "icon", "disabled", "active", "onClick", "onContextMenu", "onMouseDown", "onMouseUp", "onMouseEnter", "onMouseLeave", "onKeyDown", "className", "style", "children", "tabIndex", "data-testid", "aria-label"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
import React from 'react';
import { useSlotProps } from '@react-spectrum/utils';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from "./popper/index.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var BUTTON_KINDS = ['primary', 'secondary', 'tertiary', 'success', 'danger', 'inline', 'ghost'];
var VARIANT_KINDS = ['group-end'];
function getClassName(kind, iconOnly) {
  switch (kind) {
    case 'primary':
      return 'btn-primary';
    case 'secondary':
      return 'btn-outline-primary';
    case 'tertiary':
      return 'btn-secondary';
    case 'success':
      return 'btn-success';
    case 'danger':
      return 'btn-danger';
    case 'inline':
      return 'btn-inline';
    case 'ghost':
      return classNames('btn-link', {
        'btn-link-icon': iconOnly,
        'btn-link-icon-only': iconOnly
      });
  }
}
function getVariantClasses(kind) {
  switch (kind) {
    case 'group-end':
      return classNames('pl-2', 'pr-3');
  }
}
export var Button = /*#__PURE__*/React.forwardRef((props, ref) => {
  var kind = props.kind,
    variant = props.variant,
    _props$type = props.type,
    type = _props$type === void 0 ? 'button' : _props$type,
    tooltip = props.tooltip,
    icon = props.icon,
    _props$disabled = props.disabled,
    disabled = _props$disabled === void 0 ? false : _props$disabled,
    active = props.active,
    onClick = props.onClick,
    onContextMenu = props.onContextMenu,
    onMouseDown = props.onMouseDown,
    onMouseUp = props.onMouseUp,
    onMouseEnter = props.onMouseEnter,
    onMouseLeave = props.onMouseLeave,
    onKeyDown = props.onKeyDown,
    className = props.className,
    style = props.style,
    children = props.children,
    tabIndex = props.tabIndex,
    dataTestId = props['data-testid'],
    ariaLabel = props['aria-label'],
    rest = _objectWithoutProperties(props, _excluded);

  // Spectrum container components such as `ButtonGroup` provide
  // UNSAFE_className prop for the `button` slot via a SlotProvider (
  // https://github.com/adobe/react-spectrum/blob/%40adobe/react-spectrum%403.33.1/packages/%40react-spectrum/buttongroup/src/ButtonGroup.tsx#L104-L107)
  // This can be retrieved via `useSlotProps` to allow our buttons to be styled
  // correctly inside the container.
  var _useSlotProps = useSlotProps({}, 'button'),
    UNSAFE_className = _useSlotProps.UNSAFE_className;
  var variantClassName;
  if (variant) {
    variantClassName = getVariantClasses(variant);
  }
  var iconElem;
  if (icon) {
    iconElem = /*#__PURE__*/React.isValidElement(icon) ? icon : /*#__PURE__*/_jsx(FontAwesomeIcon, {
      icon: icon
    });
  }

  // not entirely accurate, as button can have non-visible children
  var iconOnly = iconElem != null && children == null;
  var btnClassName = getClassName(kind, iconOnly);
  var tooltipElem;
  if (tooltip !== undefined) {
    tooltipElem = typeof tooltip === 'string' ? /*#__PURE__*/_jsx(Tooltip, {
      children: tooltip
    }) : tooltip;
  }

  // use tooltip as arial-label for iconOnly buttons only
  // if tooltip is also a string and aria-label is not set
  var ariaLabelString = ariaLabel;
  if (ariaLabel === undefined && iconOnly && tooltip != null && typeof tooltip === 'string') {
    ariaLabelString = tooltip;
  }
  var button = /*#__PURE__*/_jsxs("button", _objectSpread(_objectSpread({
    "data-testid": dataTestId,
    ref: ref
    // eslint-disable-next-line react/button-has-type
    ,
    type: type,
    className: classNames('btn', btnClassName, variantClassName, {
      active
    }, className, UNSAFE_className),
    onClick: onClick,
    onContextMenu: onContextMenu,
    onMouseUp: onMouseUp,
    onMouseDown: onMouseDown,
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
    onKeyDown: onKeyDown,
    style: style,
    disabled: disabled,
    tabIndex: tabIndex,
    "aria-label": ariaLabelString
    // eslint-disable-next-line react/jsx-props-no-spreading
  }, rest), {}, {
    children: [icon && iconElem, children, tooltip != null && !disabled && tooltipElem]
  }));

  // disabled buttons tooltips need a wrapped element to receive pointer events
  // https://jakearchibald.com/2017/events-and-disabled-form-fields/

  return disabled && tooltip != null ? /*#__PURE__*/_jsxs("span", {
    className: "btn-disabled-wrapper",
    children: [button, tooltipElem]
  }) : button;
});
Button.displayName = 'Button';
export default Button;
//# sourceMappingURL=Button.js.map